import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type TenantSummary = components['schemas']['TenantSummaryResponse'];
export type TenantDetail = components['schemas']['TenantDetailResponse'];
export type TenantContact = components['schemas']['TenantContactResponse'];

/**
 * The tenant read queries behind M1-33's two screens.
 *
 * Both endpoints already exist (M1-31) and are gated on `staff:tenants.read`
 * server-side; this file adds no matching, no filtering and no ordering of its
 * own. The API decides what a search matches — reimplementing any part of that
 * here would be a second answer to the same question, and the browser's copy
 * is the one nobody notices has drifted.
 *
 * ⚠️ Every call here is **audited server-side**, including the search term.
 * That is deliberate (M1-31), and it is also why `App.tsx` turns
 * `refetchOnWindowFocus` off for this console — a support call means
 * alt-tabbing, and re-firing a tenant read on every focus would pad the trail
 * that M1-35 has to be readable.
 */

/**
 * How many rows one page asks for.
 *
 * ⚠️ The server rejects anything above `AdminTenantEndpoints.MaxPageSize`
 * (100) with a 400 — it does not clamp. The dispatch board asked for 500
 * against the same shape of cap and returned 400 for three days, because its
 * test stub answered every request with every row and never looked at
 * `pageSize`. The stub in `Tenants.test.tsx` refuses what the server refuses,
 * so a number above the cap fails a test here rather than in dev.
 */
export const TENANT_PAGE_SIZE = 25;

/** The largest page size the API will accept. Mirrors the server's constant. */
export const TENANT_MAX_PAGE_SIZE = 100;

/**
 * A page of tenants, optionally narrowed by name.
 *
 * An empty or whitespace-only term omits `q` entirely rather than sending
 * `q=`, so "cleared the box" and "never typed" are one cache entry rather than
 * two — and one audit row rather than two.
 *
 * The counters come back as `number | string` from the generated types (the
 * spec's int32 format widens that way), so they are coerced once here instead
 * of at each of the three places the pager reads them.
 */
export function useTenantSearch(term: string, page: number) {
  const q = term.trim();

  return useQuery({
    queryKey: ['admin', 'tenants', 'search', q, page],
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/admin/tenants', {
        params: { query: { ...(q ? { q } : {}), page, pageSize: TENANT_PAGE_SIZE } },
      });
      // `!response.ok`, not just `error`: openapi-fetch only fills `error`
      // when the failure body parses as the declared problem+json, so a
      // gateway 502 (an HTML page) falls straight through an error-only check
      // and this would return undefined. Same guard the customer app uses.
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/admin/tenants failed: ${response.status}`);
      }
      return {
        items: data.items,
        totalCount: Number(data.totalCount),
        page: Number(data.page),
        pageSize: Number(data.pageSize),
      };
    },
  });
}

/** How many pages a result set of this size spans. Always at least one. */
export function pageCount(totalCount: number, pageSize: number): number {
  if (pageSize <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(totalCount / pageSize));
}

/**
 * One tenant, by id.
 *
 * A tenant's page is a link a support agent pastes into a ticket, so it has to
 * stand on its own rather than only being reachable from the list.
 */
export function useTenant(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'tenants', tenantId],
    enabled: tenantId !== undefined,
    retry: false,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/admin/tenants/{id}', {
        params: { path: { id: tenantId! } },
      });
      // A 404 is a real answer, not a transport failure: it is what a stale
      // ticket link or a mistyped id looks like, and the screen should say
      // "no such tenant" rather than "something went wrong".
      if (response.status === 404) {
        throw new TenantNotFoundError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/admin/tenants/{id} failed: ${response.status}`);
      }
      return data;
    },
  });
}

export class TenantNotFoundError extends Error {
  constructor() {
    super('Tenant not found');
    this.name = 'TenantNotFoundError';
  }
}
